import { useEffect, useState } from "react";
import Select from "react-select";
import ReactQuill from "react-quill";

/* USER DEFINED COMPONENTS */
import InputField from "../../../components/input";
import { getBlogCategories } from "../../../service/api";

export const DEFAULT_CATEGORY_OPTIONS = [
  { value: "Menopause", label: "Menopause" },
  { value: "LOW-T", label: "LOW-T" },
  { value: "Ketamine", label: "Ketamine" },
  { value: "Weight Loss", label: "Weight Loss" },
  { value: "General", label: "General Health" },
];

export const STATUS_OPTIONS = [
  { value: true, label: "Active (visible on website)" },
  { value: false, label: "Hidden" },
];

const quillModules = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote"],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "blockquote",
  "link",
];

const BlogForm = ({ formik }) => {
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_CATEGORY_OPTIONS);

  useEffect(() => {
    getBlogCategories()
      .then((res) => {
        if (res?.status && Array.isArray(res?.data) && res.data.length > 0) {
          const opts = res.data.map((c) => ({ value: c.name, label: c.name, badge: c.categoryBadge }));
          setCategoryOptions(opts);
        }
      })
      .catch(() => {});
  }, []);

  const currentCategoryValue = formik.values.category;
  const currentCategoryOption =
    categoryOptions.find((opt) => opt.value === currentCategoryValue) ||
    (currentCategoryValue ? { value: currentCategoryValue, label: currentCategoryValue } : null);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-y-5 xl:gap-5 lg:gap-5">
      <div className="col-span-2">
        <div className="bg-white rounded-lg border border-authBorder">
          <div className="px-5 py-3 border-b border-authBorder">
            <h5 className="text-lg font-ibm-bold text-black">
              Blog Article Details
            </h5>
            <p className="text-sm font-ibm-regular text-greyText">
              Add the article title, short summary, and main body content.
            </p>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 gap-5">
              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Article Title <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <InputField
                      type="text"
                      name="title"
                      id="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      error={
                        formik.errors.title && formik.touched.title
                          ? true
                          : false
                      }
                      placeholder="e.g., Understanding Menopause & Care Options"
                    />
                  </div>
                  {formik.errors.title && formik.touched.title && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.title}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Short Summary / Teaser <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs font-ibm-regular text-greyText">
                    Shown on the blog cards on the Health Blogs library page.
                  </p>
                  <div className="mt-1">
                    <textarea
                      name="summary"
                      id="summary"
                      value={formik.values.summary}
                      onChange={formik.handleChange}
                      className={`w-full px-5 py-3 border ${
                        formik.errors.summary && formik.touched.summary
                          ? "border-red-500"
                          : "border-authBorder"
                      } rounded-md text-base font-ibm-regular text-black focus:outline-logoYellow min-h-[10vh]`}
                      placeholder="Brief 1-2 sentence overview of the article"
                    />
                  </div>
                  {formik.errors.summary && formik.touched.summary && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.summary}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Article Content <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 blog-editor">
                    <ReactQuill
                      theme="snow"
                      value={formik.values.content}
                      onChange={(html) =>
                        formik.setFieldValue("content", html)
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Write the full health blog article content here..."
                    />
                  </div>
                  {formik.errors.content && formik.touched.content && (
                    <div className="text-red-500 text-xs mt-1">
                      {formik.errors.content}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SEO DETAILS SECTION */}
        <div className="bg-white rounded-lg border border-authBorder mt-5">
          <div className="px-5 py-3 border-b border-authBorder">
            <h5 className="text-lg font-ibm-bold text-black">
              SEO Details (Optional)
            </h5>
            <p className="text-sm font-ibm-regular text-greyText">
              Custom slug and meta tags for search engines.
            </p>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-5">
              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    Slug
                  </label>
                  <div className="mt-1">
                    <InputField
                      type="text"
                      name="slug"
                      id="slug"
                      value={formik.values.slug}
                      onChange={formik.handleChange}
                      placeholder="e.g., menopause-care-guide"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-1">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    SEO Title
                  </label>
                  <div className="mt-1">
                    <InputField
                      type="text"
                      name="seoTitle"
                      id="seoTitle"
                      value={formik.values.seoTitle}
                      onChange={formik.handleChange}
                      placeholder="SEO Meta Title"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-2">
                <div className="form-group">
                  <label className="text-sm font-ibm-regular text-greyText">
                    SEO Description
                  </label>
                  <div className="mt-1">
                    <textarea
                      name="seoDescription"
                      id="seoDescription"
                      value={formik.values.seoDescription}
                      onChange={formik.handleChange}
                      className="w-full px-4 py-2 border border-authBorder rounded-md focus:outline-none focus:border-logoYellow transition-colors duration-300 min-h-[100px]"
                      placeholder="SEO Meta Description"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-1">
        {/* DISPLAY & CATEGORY SECTION */}
        <div className="bg-white rounded-lg border border-authBorder">
          <div className="px-5 py-3 border-b border-authBorder">
            <h5 className="text-lg font-ibm-bold text-black">Settings</h5>
            <p className="text-sm font-ibm-regular text-greyText">
              Category and publishing controls.
            </p>
          </div>
          <div className="p-5">
            <div className="form-group">
              <label className="text-sm font-ibm-regular text-greyText">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <Select
                  options={categoryOptions}
                  placeholder="Select category"
                  classNamePrefix="dropdown"
                  name="category"
                  id="category"
                  value={currentCategoryOption}
                  onChange={(selectedOption) => {
                    formik.setFieldValue("category", selectedOption?.value || "");
                    if (selectedOption?.badge) {
                      formik.setFieldValue("categoryBadge", selectedOption.badge);
                    }
                  }}
                />
              </div>
            </div>

            <div className="form-group mt-4">
              <label className="text-sm font-ibm-regular text-greyText">
                Author
              </label>
              <div className="mt-1">
                <InputField
                  type="text"
                  name="author"
                  id="author"
                  value={formik.values.author}
                  onChange={formik.handleChange}
                  placeholder="e.g., WellCare Clinical Team"
                />
              </div>
            </div>

            <div className="form-group mt-4">
              <label className="text-sm font-ibm-regular text-greyText">
                Read Time
              </label>
              <div className="mt-1">
                <InputField
                  type="text"
                  name="readTime"
                  id="readTime"
                  value={formik.values.readTime}
                  onChange={formik.handleChange}
                  placeholder="e.g., 5 min read"
                />
              </div>
            </div>

            <div className="form-group mt-4">
              <label className="text-sm font-ibm-regular text-greyText">
                Status
              </label>
              <div className="relative mt-1">
                <Select
                  options={STATUS_OPTIONS}
                  placeholder="Select status"
                  classNamePrefix="dropdown"
                  name="isActive"
                  id="isActive"
                  value={STATUS_OPTIONS.find(
                    (option) => option.value === formik.values.isActive
                  )}
                  onChange={(selectedOption) =>
                    formik.setFieldValue("isActive", selectedOption?.value)
                  }
                />
              </div>
            </div>

            <div className="form-group mt-4">
              <label className="text-sm font-ibm-regular text-greyText">
                Display Order
              </label>
              <div className="mt-1">
                <InputField
                  type="number"
                  name="sortOrder"
                  id="sortOrder"
                  value={formik.values.sortOrder}
                  onChange={formik.handleChange}
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogForm;
